import { useState } from 'react';
import moment from 'moment';
import { useServerStorage, EStorageType } from './useServerStorage';
import { navigate } from '@home/utils/utils';
import { getFileNameAndGameInfo } from '@op/pages/download/utils';
import { getServerGameInfo } from '@op/utils/game';
import { notification } from 'tea-component';
import { setCursorLoading } from '../utils/cursorLoading';

/** 旧版 downloadAction 中 simpleName 拼接文件名所需，复刻同名逻辑 */
async function getGameNameForLegacy(gameId: string): Promise<string> {
  try {
    const sessionGameInfo = JSON.parse(
      localStorage.getItem('OpinionGameInfo') || '{}',
    );
    if (gameId === sessionGameInfo?.edition_unified_id) {
      return sessionGameInfo?.entity_name || '';
    }
  } catch (error) {
    /* ignore */
  }
  const game = await getServerGameInfo(gameId);
  return game?.entity_name;
}

/** URL 中下载参数的 id key */
export const DOWNLOAD_ID_KEY = 'id';

/** 旧版下载页（/opinion/download）所需参数（与旧 downloadAction 存储结构一致） */
type TDownloadLegacyParams = {
  /** 文件名 */
  fileName?: string;
  /** 下载请求参数（旧结构中的 params 字段，即 downLoadParams） */
  params: any;
  /** 旧版下载函数名（downloadFuncs / downloadWithLists 中的 key），默认 'downloadKeyMetrics' */
  downloadFunc?: string;
  limit?: number;
  total?: number;
  /** sheet 名，决定旧下载页 columnsMap 取哪套列 */
  sheetName?: string;
  simpleName?: string;
  align?: string;
};

type TUseDownloadParamsOptions = {
  /** 最大允许行数，用于前置拦截（不传则不做限制） */
  maxRows?: number;
  /** 超出时的提示文案 */
  exceedMessage?: string;
};

type TDownloadParams = {
  key: string;
  fileName: string;
  filter: any;
  auxiliaryParams?: any;
  data?: any;
  needFilterTime?: boolean;
};

/** download 方法的返回值 */
type TDownloadResult = {
  /** 是否成功发起下载 */
  success: boolean;
  /** 失败原因（success=false 时才有值） */
  reason?: 'exceed_limit' | 'save_failed';
};

type TUseDownloadParamsReturn = {
  /**
   * 前置检查 + 发起下载（新版下载页 /opinion/NewDownload）
   * @param params 下载参数
   * @param currentTotal 当前数据总量（页面已知），用于前置拦截
   * @returns { success, reason } 下载结果
   */
  download: (
    params: TDownloadParams,
    currentTotal?: number,
  ) => Promise<TDownloadResult>;
  /**
   * 旧版下载页兼容入口（跳 /opinion/download）
   * 复用 hook 内部的服务端/本地存储能力，但写入旧下载页可消费的旧结构（downloadFunc/sheetName/...）
   * 用于：页面本身仍走旧版下载页（如 CompareKeyMetrics 的专用接口 downloadKeyMetrics）
   */
  downloadLegacy: (params: TDownloadLegacyParams) => Promise<TDownloadResult>;
  /** 从当前页面 URL 读取下载参数（下载页面使用，一站式） */
  readParams: () => Promise<any | null>;
  /** 是否正在保存（仅覆盖存储请求阶段，可用于禁用下载按钮） */
  saving: boolean;
  /** 是否正在读取 */
  loading: boolean;
  /**
   * 完整下载流程 loading（点击下载立即置 true，到整个下载流程结束置 false）
   * 覆盖：数据量检查 + game 接口请求（getServerGameInfo）+ 存储保存 + 跳转前的全部耗时
   */
  downloading: boolean;
};

/**
 * 下载参数管理 Hook（含数据量前置检查）
 *
 * 用法（发起方页面）：
 *   const { download, saving, downloading } = useDownloadParams({
 *     maxRows: 100000,
 *     exceedMessage: '数据量过大，请缩小筛选范围',
 *   });
 *
 *   const handleDownload = async () => {
 *     const { success, reason } = await download({ key, fileName, filter }, totalCount);
 *     if (!success && reason === 'exceed_limit') {
 *       // 调用方可以自己做额外处理
 *     }
 *   };
 *
 *   // downloading 覆盖完整下载流程（含 game 接口请求），点击下载立即转圈、结束后停止
 *   <Button loading={downloading || saving} onClick={handleDownload}>下载</Button>
 *
 * 用法（下载页面）：
 *   const { readParams, loading } = useDownloadParams();
 *   const params = await readParams();
 */
export function useDownloadParams(
  options: TUseDownloadParamsOptions = {},
): TUseDownloadParamsReturn {
  const { maxRows, exceedMessage } = options;

  const { save, getFromUrl, saving, loading } = useServerStorage({
    type: EStorageType.Download,
    fallback: true,
  });

  /** 完整下载流程 loading（含 game 接口请求等，覆盖 saving 之外的耗时） */
  const [downloading, setDownloading] = useState(false);

  const download = async (
    params: TDownloadParams,
    currentTotal?: number,
  ): Promise<TDownloadResult> => {
    const { key, fileName, filter, auxiliaryParams, data, needFilterTime } =
      params;

    setDownloading(true);
    // 下载期间全局鼠标 loading，结束后恢复
    setCursorLoading(true);
    try {
      // 1. 前置检查：数据量超限
      if (
        maxRows !== undefined &&
        currentTotal !== undefined &&
        currentTotal > maxRows
      ) {
        if (exceedMessage) {
          notification.error({ description: exceedMessage });
        }
        return { success: false, reason: 'exceed_limit' };
      }

      // 2. 通过检查，发起下载（含 game 接口请求）
      const { newFileName, gameInfo } = await getFileNameAndGameInfo(
        fileName,
        filter,
        needFilterTime,
      );

      const storageData = {
        key,
        fileName: newFileName,
        filter,
        auxiliaryParams,
        gameInfo,
        data,
      };

      const result = await save(storageData);
      if (!result) {
        return { success: false, reason: 'save_failed' };
      }

      navigate(
        `/opinion/NewDownload?${DOWNLOAD_ID_KEY}=${result.id}&_s=${result.source}&gameid=${gameInfo?.edition_unified_id}`,
        { jumpType: 'Window' },
      );
      return { success: true };
    } finally {
      setDownloading(false);
      setCursorLoading(false);
    }
  };

  /**
   * 旧版下载页兼容入口（跳 /opinion/download）
   * 复用 save（服务端存储 + 本地 fallback），写入旧下载页可消费的旧结构
   */
  const downloadLegacy = async (
    params: TDownloadLegacyParams,
  ): Promise<TDownloadResult> => {
    const {
      fileName: paramsFileName,
      params: downLoadParams,
      downloadFunc = 'downloadKeyMetrics',
      limit = Infinity,
      total = -Infinity,
      sheetName = 'Sheet',
      simpleName,
      align = 'auto',
    } = params;

    setDownloading(true);
    // 下载期间全局鼠标 loading，结束后恢复
    setCursorLoading(true);
    try {
      // 复刻旧 downloadAction 的文件名拼接逻辑（含 game 接口请求）
      let fileName = paramsFileName;
      if (simpleName) {
        const gameName = await getGameNameForLegacy(
          downLoadParams.edition_unified_id,
        );
        fileName = `Opinions_${gameName}_${simpleName}_${moment(
          downLoadParams.start_time,
        ).format('YYYYMMDD')}_${moment(downLoadParams.end_time).format(
          'YYYYMMDD',
        )}`;
      }
      fileName = fileName?.replace(/[\\/:*?."<>|]/g, '');

      // 与旧 downloadAction 存储结构完全一致
      const storageData = {
        fileName,
        params: downLoadParams,
        downloadFunc,
        limit,
        total,
        sheetName,
        align,
        downloadTime: new Date().getTime(),
      };

      const result = await save(storageData);
      if (!result) {
        return { success: false, reason: 'save_failed' };
      }

      navigate(
        `/opinion/download?${DOWNLOAD_ID_KEY}=${result.id}&_s=${result.source}`,
        { jumpType: 'Window' },
      );
      return { success: true };
    } finally {
      setDownloading(false);
      setCursorLoading(false);
    }
  };

  const readParams = async () => {
    return getFromUrl(DOWNLOAD_ID_KEY);
  };

  return { download, downloadLegacy, readParams, saving, loading, downloading };
}
